import { App, Form } from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CustomActionButtons, CustomModal, CustomSelect } from "sipli-fleet-core/components";
import {
  useMutationCreateModelProperty,
  useMutationUpdateModelProperty,
  useQueryGetPropertiesByMake,
  useQueryGetUnitModelsByMake,
} from "src/queries/administrator/modelProperty.queries";
import { useQueryGetUnitBrandLookup } from "src/queries/administrationSetup.queries";
import type {
  ModelPropertyPayload,
  ModelPropertyRecord,
  NamedReference,
} from "src/services/administrator/modelPropertyService";

interface AddModelPropertiesFormModalProps {
  open: boolean;
  record?: ModelPropertyRecord | null;
  onClose: () => void;
}

interface AddModelPropertiesFormValues {
  makeId?: string | number;
  modelId?: string | number;
  propertyId?: string | number;
  active: string;
}

interface SelectOption {
  value: string | number | undefined;
  label: string;
}

const isActive = (value: unknown) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") return ["active", "true", "1"].includes(value.toLowerCase());
  return false;
};

const getLookupItems = (payload: unknown): NamedReference[] => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  const lookupPayload = payload as { response?: unknown; data?: unknown; items?: unknown };
  if (Array.isArray(lookupPayload.response)) return lookupPayload.response;
  if (Array.isArray(lookupPayload.data)) return lookupPayload.data;
  if (Array.isArray(lookupPayload.items)) return lookupPayload.items;
  return [];
};

const toOptions = (payload: unknown) =>
  getLookupItems(payload).map((item) => ({
    value: item.id,
    label:
      item.propertyNameComm ??
      item.name ??
      item.propertyNameInternal ??
      item.code ??
      String(item.id),
  }));

const getUnitBrand = (record?: ModelPropertyRecord | null) => record?.make ?? record?.model?.make;

const toSelectedOption = (item?: NamedReference | null): SelectOption | null => {
  if (!item?.id) return null;
  return {
    value: item.id,
    label:
      item.propertyNameComm ??
      item.name ??
      item.propertyNameInternal ??
      item.code ??
      String(item.id),
  };
};

const mergeSelectedOption = (options: SelectOption[], selected: SelectOption | null) => {
  if (!selected?.value) return options;
  const hasSelectedOption = options.some(
    (option) => String(option.value) === String(selected.value)
  );
  return hasSelectedOption ? options : [selected, ...options];
};

const getErrorMessage = (error: unknown) => {
  if (!error || typeof error !== "object") return undefined;

  const response = (error as { response?: unknown }).response;
  if (!response || typeof response !== "object") return undefined;

  const data = (response as { data?: unknown }).data;
  if (!data || typeof data !== "object") return undefined;

  const responseMessage = (data as { message?: unknown }).message;
  return typeof responseMessage === "string" && responseMessage.trim()
    ? responseMessage
    : undefined;
};

const AddModelPropertiesFormModal = ({
  open,
  record,
  onClose,
}: AddModelPropertiesFormModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<AddModelPropertiesFormValues>();
  const isEditing = Boolean(record?.id);
  const makeId = Form.useWatch("makeId", form);

  const unitMakeQuery = useQueryGetUnitBrandLookup(open);
  const unitModelsQuery = useQueryGetUnitModelsByMake(makeId, { enabled: open && Boolean(makeId) });
  const propertiesQuery = useQueryGetPropertiesByMake(makeId, { enabled: open && Boolean(makeId) });
  const createMutation = useMutationCreateModelProperty();
  const updateMutation = useMutationUpdateModelProperty();

  const statusOptions = useMemo(
    () => [
      { label: t("active"), value: "true" },
      { label: t("inactive"), value: "false" },
    ],
    [t]
  );

  const selectedMakeOption = useMemo(() => toSelectedOption(getUnitBrand(record)), [record]);
  const selectedModelOption = useMemo(() => toSelectedOption(record?.model), [record?.model]);
  const selectedPropertyOption = useMemo(
    () => toSelectedOption(record?.property),
    [record?.property]
  );

  const makeOptions = useMemo(
    () => mergeSelectedOption(toOptions(unitMakeQuery.data?.data), selectedMakeOption),
    [selectedMakeOption, unitMakeQuery.data?.data]
  );
  const modelOptions = useMemo(
    () => mergeSelectedOption(toOptions(unitModelsQuery.data?.data), selectedModelOption),
    [selectedModelOption, unitModelsQuery.data?.data]
  );
  const propertyOptions = useMemo(
    () => mergeSelectedOption(toOptions(propertiesQuery.data?.data), selectedPropertyOption),
    [propertiesQuery.data?.data, selectedPropertyOption]
  );

  useEffect(() => {
    if (!open) return;

    form.setFieldsValue({
      makeId: getUnitBrand(record)?.id,
      modelId: record?.model?.id,
      propertyId: record?.property?.id,
      active: String(record ? isActive(record.active) : true),
    });
  }, [form, open, record]);

  const handleMakeChange = () => {
    form.setFieldsValue({
      modelId: undefined,
      propertyId: undefined,
    });
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload: ModelPropertyPayload = {
      makeId: values.makeId!,
      modelId: values.modelId!,
      propertyId: values.propertyId!,
      active: values.active === "true",
    };

    try {
      if (isEditing && record?.id) {
        await updateMutation.mutateAsync({ id: record.id, payload });
        message.success(t("model_property_updated"));
      } else {
        await createMutation.mutateAsync(payload);
        message.success(t("model_property_created"));
      }
      handleClose();
    } catch (error) {
      const responseMessage = (error as { response?: { data?: { message?: unknown } } })?.response
        ?.data?.message;
      message.error(
        typeof responseMessage === "string" && responseMessage.trim()
          ? responseMessage
          : t("setup_save_failed")
      );
    }
  };

  return (
    <CustomModal
      open={open}
      title={t(isEditing ? "edit_add_model_properties" : "add_model_properties")}
      onCancel={handleClose}
      destroyOnHidden
      width={680}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        className="grid grid-cols-1 pt-4 gap-x-4 sm:grid-cols-2"
      >
        <Form.Item
          name="makeId"
          label={t("unit_make")}
          rules={[{ required: true, message: t("unit_make_required") }]}
          className="mb-3!"
        >
          <CustomSelect
            options={makeOptions}
            placeholder={t("select_unit_make")}
            loading={unitMakeQuery.isLoading}
            disabled={unitMakeQuery.isLoading}
            showSearch
            optionFilterProp="label"
            onChange={handleMakeChange}
          />
        </Form.Item>

        <Form.Item
          name="modelId"
          label={t("unit_model")}
          rules={[{ required: true, message: t("unit_model_required") }]}
          className="mb-3!"
        >
          <CustomSelect
            options={modelOptions}
            placeholder={t("select_unit_model")}
            loading={unitModelsQuery.isLoading}
            disabled={!makeId || unitModelsQuery.isLoading}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item
          name="propertyId"
          label={t("property")}
          rules={[{ required: true, message: t("property_required") }]}
          className="mb-3!"
        >
          <CustomSelect
            options={propertyOptions}
            placeholder={t("select_property")}
            loading={propertiesQuery.isLoading}
            disabled={!makeId || propertiesQuery.isLoading}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item
          name="active"
          label={t("status")}
          rules={[{ required: true, message: t("status_required") }]}
          className="mb-3!"
        >
          <CustomSelect options={statusOptions} placeholder={t("select")} />
        </Form.Item>

        <Form.Item className="sm:col-span-2 mb-0!">
          <CustomActionButtons
            onCancel={handleClose}
            onOk={handleSubmit}
            loading={createMutation.isPending || updateMutation.isPending}
            okProps={{ htmlType: "button" }}
            cancelBtnLabel="cancel"
            okBtnLabel={isEditing ? "update" : "add"}
          />
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export default AddModelPropertiesFormModal;
